const SERVICE_WORKER_PATH = '/notification-sw.js'
const PESO_RATE_PER_KWH = 11.743

let registrationPromise = null

export function isNotificationSupported() {
  return typeof window !== 'undefined'
    && 'Notification' in window
    && 'serviceWorker' in navigator
}

export function getNotificationPermission() {
  if (!isNotificationSupported()) {
    return 'unsupported'
  }

  return Notification.permission
}

export function registerNotificationServiceWorker() {
  if (!isNotificationSupported()) {
    return Promise.resolve(null)
  }

  if (!registrationPromise) {
    registrationPromise = navigator.serviceWorker.register(SERVICE_WORKER_PATH)
  }

  return registrationPromise
}

export async function requestNotificationPermission() {
  if (!isNotificationSupported()) {
    return 'unsupported'
  }

  await registerNotificationServiceWorker()

  if (Notification.permission === 'granted') {
    return 'granted'
  }

  if (Notification.permission === 'denied') {
    return 'denied'
  }

  return Notification.requestPermission()
}

function unwrapBoardInfo(boardInfo) {
  if (!boardInfo || typeof boardInfo !== 'object') {
    return {}
  }

  if (boardInfo.success !== undefined && boardInfo.data && typeof boardInfo.data === 'object') {
    return boardInfo.data
  }

  return boardInfo
}

function toNumber(value) {
  if (typeof value === 'number' && Number.isFinite(value)) {
    return value
  }

  if (typeof value === 'string') {
    const parsed = Number(value)

    return Number.isFinite(parsed) ? parsed : null
  }

  return null
}

function getPowerValues(boardInfo) {
  const payload = unwrapBoardInfo(boardInfo)
  const data = payload?.data || {}
  const importValue = toNumber(data.powerImport ?? payload.devicedata?.meter?.import?.W)
  const exportValue = toNumber(data.powerExport ?? payload.devicedata?.meter?.export?.W)

  if (importValue === null || exportValue === null) {
    return null
  }

  return {
    deviceId: payload?.config?.Device?.ID || data.DeviceID || 'Device',
    exportValue,
    importValue,
    timestamp: toNumber(data.timestamp ?? payload?.devicedata?.meter?.timestamp) || Date.now(),
  }
}

export function getPowerNotificationState(boardInfo) {
  const values = getPowerValues(boardInfo)

  if (!values) {
    return null
  }

  if (values.importValue > values.exportValue) {
    return {
      ...values,
      level: 'critical',
      title: 'CNERGI demand alert',
      body: `${values.deviceId}: import ${values.importValue.toFixed(2)} kW is higher than solar export ${values.exportValue.toFixed(2)} kW. Consumption is exceeding solar capacity.`,
    }
  }

  if (values.exportValue > values.importValue) {
    return {
      ...values,
      level: 'stable',
      title: 'CNERGI stable consumption',
      body: `${values.deviceId}: solar export ${values.exportValue.toFixed(2)} kW is higher than import ${values.importValue.toFixed(2)} kW. Consumption is stable again.`,
    }
  }

  return {
    ...values,
    level: 'balanced',
    title: 'CNERGI balanced consumption',
    body: `${values.deviceId}: import and export are balanced at ${values.importValue.toFixed(2)} kW.`,
  }
}

export async function showPowerNotification(notificationState) {
  if (!notificationState || getNotificationPermission() !== 'granted') {
    return false
  }

  const registration = await registerNotificationServiceWorker()
  const timestampLabel = new Date(notificationState.timestamp).toLocaleString()
  const options = {
    badge: '/iconCNI.png',
    body: `${notificationState.body}\n${timestampLabel}`,
    data: {
      level: notificationState.level,
      timestamp: notificationState.timestamp,
    },
    icon: '/iconCNI.png',
    tag: `cnergi-${notificationState.deviceId}-power-status`,
  }

  if (registration?.showNotification) {
    await registration.showNotification(notificationState.title, options)
    return true
  }

  new Notification(notificationState.title, options)
  return true
}

export function estimateSavingsFromKwh(kwh) {
  const value = toNumber(kwh)

  return value === null ? 0 : value * PESO_RATE_PER_KWH
}
